/**
 * Role Play Session Detail (CRM web) — debrief viewer for a completed session.
 * 6-pillar scores, overall, summary, next drill recommendation.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Play, Sparkles, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function pillarColor(score) {
    if (score >= 85) return '#1DA063';
    if (score >= 70) return '#F2A423';
    return '#E5484D';
}

export default function RolePlaySessionDetail() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    const load = async () => {
        try {
            const res = await axios.get(`${API}/role-play/sessions/${sessionId}`, {
                headers: getAuthHeader(),
            });
            setSession(res.data);
            if (res.data?.status === 'processing') {
                setTimeout(load, 3000);
            }
        } catch (e) {
            toast.error('Failed to load debrief');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [sessionId]);

    const regenerate = async () => {
        setGenerating(true);
        try {
            await axios.post(`${API}/role-play/sessions/${sessionId}/debrief`, {}, {
                headers: getAuthHeader(),
            });
            await load();
        } catch (e) {
            toast.error('Debrief failed');
        } finally {
            setGenerating(false);
        }
    };

    if (loading || !session) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    const pillars = session.pillar_scores || [];
    const overall = session.overall_score ?? 0;
    const avatarNames = (session.avatars || []).map(a => a.name).join(' & ') || 'Avatars';

    return (
        <div className="max-w-5xl mx-auto p-6">
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate('/practice/role-play')}
                    className="p-2 hover:bg-stone-100 rounded-lg"
                >
                    <ArrowLeft className="w-4 h-4 text-stone-500" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Role Play Debrief
                    </h1>
                    <p className="text-sm text-stone-500">
                        {Math.round((session.duration_sec || 0) / 60)} min ·
                        vs. {avatarNames} · {session.difficulty}
                    </p>
                </div>
                <div className="flex-1" />
                <button
                    onClick={() => navigate('/practice/role-play/new')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800"
                >
                    <Play className="w-4 h-4" /> Run again
                </button>
            </div>

            {/* Hero */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div
                    className="lg:col-span-2 rounded-2xl p-6 text-white"
                    style={{ background: 'linear-gradient(135deg, #2d1a6e, #1a0f3d)' }}
                >
                    <div className="text-xs font-bold tracking-widest uppercase opacity-70 mb-2">Overall score</div>
                    <div className="flex items-baseline gap-3">
                        <span className="font-normal tracking-tight" style={{ fontFamily: '"Instrument Serif", serif', fontSize: 72, lineHeight: 1 }}>
                            {overall}
                        </span>
                        <span className="text-sm opacity-60">/ 100</span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
                        {session.debrief_summary || 'Debrief is being generated — this typically takes ~15 seconds.'}
                    </p>
                    {session.debrief_recommended_drill && (
                        <div
                            className="mt-4 p-3 rounded-lg text-sm"
                            style={{ background: 'rgba(255,255,255,0.1)' }}
                        >
                            💡 <b>Next drill:</b> {session.debrief_recommended_drill}
                        </div>
                    )}
                </div>

                <div className="bg-white border border-stone-200 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3 text-stone-500">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-xs font-bold tracking-widest uppercase">Trend</span>
                    </div>
                    <svg width="100%" height="80" viewBox="0 0 220 80">
                        <polyline
                            points="0,60 40,55 80,50 120,45 160,35 220,30"
                            fill="none" stroke="#5D3FD3" strokeWidth="2"
                        />
                    </svg>
                    <div className="text-xs text-stone-500 mt-2">
                        {session.duration_sec ? `${Math.round(session.duration_sec / 60)}-min session` : 'Session complete'}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-stone-400" />
                <h2 className="text-xs font-bold tracking-widest uppercase text-stone-500">
                    Six pillars of coaching
                </h2>
            </div>

            {session.status === 'processing' && (
                <div
                    className="mb-4 p-4 rounded-xl flex items-center gap-2.5"
                    style={{ background: '#F4F0FF', border: '1px solid #D4C7FF' }}
                >
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#5D3FD3' }} />
                    <span className="text-sm" style={{ color: '#4527A0' }}>
                        AI is scoring your conversation against the 6 pillars…
                    </span>
                </div>
            )}

            {pillars.length === 0 && session.status !== 'processing' ? (
                <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
                    <p className="text-sm text-stone-500 mb-4">No pillar scores available yet.</p>
                    <button
                        onClick={regenerate}
                        disabled={generating}
                        className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50"
                    >
                        {generating ? 'Generating…' : '✨ Generate debrief'}
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {pillars.map(p => {
                        const c = pillarColor(p.score);
                        return (
                            <div key={p.pillar} className="bg-white border border-stone-200 rounded-xl p-4">
                                <div className="flex items-center mb-2">
                                    <span className="text-sm font-bold">{p.pillar}</span>
                                    <div className="flex-1" />
                                    <span className="text-sm font-bold" style={{ color: c }}>{p.score}%</span>
                                </div>
                                <div className="h-1 rounded-full bg-stone-100 overflow-hidden mb-3">
                                    <div
                                        className="h-full rounded-full"
                                        style={{ width: `${p.score}%`, background: c }}
                                    />
                                </div>
                                <div className="text-[10px] font-bold tracking-widest uppercase text-stone-400 mb-1">Feedback</div>
                                <p className="text-xs text-stone-700 leading-relaxed mb-2">{p.feedback}</p>
                                <div className="text-[10px] font-bold tracking-widest uppercase text-stone-400 mb-1">Improvement</div>
                                <p className="text-xs text-stone-700 leading-relaxed">{p.improvement}</p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
